import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { APP_TOUR_STEPS } from './steps';
import { matchesExpectedRoute, resolveTourTarget } from './tour-target';
import type { TourStepDef } from './types';
import { waitForElement } from './wait-for-element';

export interface AppTourEngineCallbacks {
  t: (key: string) => string;
  isMobile: boolean;
  getPathname: () => string;
  navigate: (path: string) => void;
  onComplete: () => void;
  onSkip: () => void;
  onStepChange: (index: number) => void;
}

export class AppTourEngine {
  private driverInstance: Driver | null = null;

  private currentIndex = 0;

  private destroyed = false;

  private routeListener: (() => void) | null = null;

  private highlightCleanup: (() => void) | null = null;

  constructor(private readonly callbacks: AppTourEngineCallbacks) {}

  get stepIndex(): number {
    return this.currentIndex;
  }

  get isRunning(): boolean {
    return this.driverInstance?.isActive() ?? false;
  }

  async start(fromIndex = 0): Promise<void> {
    this.destroyed = false;
    this.currentIndex = fromIndex;
    this.callbacks.onStepChange(fromIndex);
    await this.showStep(fromIndex);
  }

  async showStep(index: number): Promise<void> {
    if (this.destroyed) {
      return;
    }

    const step = APP_TOUR_STEPS[index];
    if (!step) {
      this.finish('completed');
      return;
    }

    this.currentIndex = index;
    this.callbacks.onStepChange(index);

    if (step.route && !this.isOnRoute(step.route)) {
      this.callbacks.navigate(step.route);
      await this.delay(120);
    }

    const selector = resolveTourTarget(step.target, this.callbacks.isMobile);
    const element = await waitForElement(selector);

    if (this.destroyed) {
      return;
    }

    if (!element) {
      if (index < APP_TOUR_STEPS.length - 1) {
        await this.showStep(index + 1);
      } else {
        this.finish('completed');
      }
      return;
    }

    this.destroyDriver();
    this.setupRouteListener(step);

    const isLast = index === APP_TOUR_STEPS.length - 1;
    const { t } = this.callbacks;
    const isRouteStep = step.advanceOn === 'route';
    const isNavTarget = /nav-(eventos|chats|perfil|ajustes)/.test(selector);

    this.driverInstance = driver({
      animate: true,
      overlayColor: '#152238',
      overlayOpacity: isRouteStep ? 0.78 : 0.65,
      stagePadding: isRouteStep && isNavTarget ? 14 : isRouteStep ? 10 : 8,
      stageRadius: 12,
      allowClose: true,
      smoothScroll: true,
      popoverClass: `biavent-tour-popover${isRouteStep ? ' biavent-tour-popover--route' : ''}`,
      showProgress: true,
      progressText: t('appTour.controls.progress')
        .replace('{{current}}', String(index + 1))
        .replace('{{total}}', String(APP_TOUR_STEPS.length)),
      showButtons: ['previous', 'next', 'close'],
      nextBtnText: t('appTour.controls.next'),
      ...(isLast ? { doneBtnText: t('appTour.controls.done') } : {}),
      prevBtnText: t('appTour.controls.prev'),
      disableActiveInteraction: step.advanceOn === 'route' ? false : undefined,
      onCloseClick: () => {
        this.finish('skipped');
      },
      onPrevClick: () => {
        if (index > 0) {
          void this.showStep(index - 1);
        }
      },
      onNextClick: () => {
        if (step.advanceOn === 'route' && step.expectedRoute) {
          if (!matchesExpectedRoute(this.callbacks.getPathname(), step.expectedRoute)) {
            return;
          }
        }

        if (isLast) {
          this.finish('completed');
          return;
        }

        void this.showStep(index + 1);
      },
      onHighlighted: (element) => {
        this.prepareInteractiveHighlight(element, step, t('appTour.controls.tapHere'));
      },
      onPopoverRender: (popover) => {
        this.injectSkipButton(popover.footerButtons, t('appTour.controls.skip'));
        this.applyNextButtonLabel(popover.nextButton, isLast, t);
        this.syncPreviousButtonState(popover.previousButton, index);
        this.syncNextButtonState(step, popover.nextButton, isLast);
      },
      steps: [
        {
          element: selector,
          disableActiveInteraction: step.advanceOn === 'route' ? false : true,
          popover: {
            title: t(step.titleKey),
            description: t(step.bodyKey),
            side: step.side ?? 'bottom',
            align: 'center',
          },
        },
      ],
    });

    this.driverInstance.drive();
  }

  handleRouteChange(pathname: string): void {
    const step = APP_TOUR_STEPS[this.currentIndex];
    if (!step || step.advanceOn !== 'route' || !step.expectedRoute) {
      return;
    }

    if (!matchesExpectedRoute(pathname, step.expectedRoute)) {
      return;
    }

    if (this.currentIndex >= APP_TOUR_STEPS.length - 1) {
      return;
    }

    void this.showStep(this.currentIndex + 1);
  }

  destroy(): void {
    this.destroyed = true;
    this.clearRouteListener();
    this.destroyDriver();
  }

  private finish(status: 'completed' | 'skipped'): void {
    this.destroy();
    if (status === 'completed') {
      this.callbacks.onComplete();
    } else {
      this.callbacks.onSkip();
    }
  }

  private destroyDriver(): void {
    this.clearHighlightCleanup();
    if (this.driverInstance?.isActive()) {
      this.driverInstance.destroy();
    }
    this.driverInstance = null;
    this.clearRouteListener();
  }

  private setupRouteListener(step: TourStepDef): void {
    this.clearRouteListener();

    if (step.advanceOn !== 'route' || !step.expectedRoute) {
      return;
    }

    this.routeListener = () => {
      const popover = document.querySelector('.biavent-tour-popover');
      const nextButton = popover?.querySelector(
        '.driver-popover-next-btn',
      ) as HTMLButtonElement | null;

      if (nextButton) {
        const canAdvance = matchesExpectedRoute(
          this.callbacks.getPathname(),
          step.expectedRoute!,
        );
        nextButton.disabled = !canAdvance;
        nextButton.classList.toggle('biavent-tour-btn--disabled', !canAdvance);
      }
    };

    window.addEventListener('biavent-tour-route-change', this.routeListener);
    this.routeListener();
  }

  private clearRouteListener(): void {
    if (this.routeListener) {
      window.removeEventListener('biavent-tour-route-change', this.routeListener);
      this.routeListener = null;
    }
  }

  private injectSkipButton(container: HTMLElement, label: string): void {
    if (container.querySelector('.biavent-tour-skip-btn')) {
      return;
    }

    const skipButton = document.createElement('button');
    skipButton.type = 'button';
    skipButton.className = 'biavent-tour-skip-btn';
    skipButton.textContent = label;
    skipButton.addEventListener('click', () => {
      this.finish('skipped');
    });
    container.prepend(skipButton);
  }

  private prepareInteractiveHighlight(
    element: Element | undefined,
    step: TourStepDef,
    tapLabel: string,
  ): void {
    this.clearHighlightCleanup();

    if (!element || step.advanceOn !== 'route') {
      return;
    }

    const target = element as HTMLElement;
    target.classList.remove('driver-no-interaction');
    target.classList.add('biavent-tour-tap-target');
    document.body.classList.add('biavent-tour-awaiting-tap');

    const restoredStyles: Array<{
      el: HTMLElement;
      zIndex: string;
      backdropFilter: string;
      webkitBackdropFilter: string;
    }> = [];

    const elevateShell = (shell: HTMLElement) => {
      restoredStyles.push({
        el: shell,
        zIndex: shell.style.zIndex,
        backdropFilter: shell.style.backdropFilter,
        webkitBackdropFilter: shell.style.webkitBackdropFilter,
      });
      shell.style.zIndex = '10002';
      shell.style.backdropFilter = 'none';
      shell.style.webkitBackdropFilter = 'none';
    };

    const navShell = target.closest('.bottom-navigation, .top-navigation');
    if (navShell instanceof HTMLElement) {
      elevateShell(navShell);
    }

    const removeTapIndicator = this.mountTapIndicator(target, tapLabel);

    const ensureRoute = () => {
      if (!step.expectedRoute) {
        return;
      }

      if (matchesExpectedRoute(this.callbacks.getPathname(), step.expectedRoute)) {
        return;
      }

      this.callbacks.navigate(step.expectedRoute);
    };

    target.addEventListener('click', ensureRoute);

    this.highlightCleanup = () => {
      target.removeEventListener('click', ensureRoute);
      target.classList.remove('biavent-tour-tap-target');
      document.body.classList.remove('biavent-tour-awaiting-tap');
      removeTapIndicator();
      for (const snapshot of restoredStyles) {
        snapshot.el.style.zIndex = snapshot.zIndex;
        snapshot.el.style.backdropFilter = snapshot.backdropFilter;
        snapshot.el.style.webkitBackdropFilter = snapshot.webkitBackdropFilter;
      }
    };
  }

  private mountTapIndicator(target: HTMLElement, label: string): () => void {
    const indicator = document.createElement('div');
    indicator.className = 'biavent-tour-tap-indicator';
    indicator.setAttribute('aria-hidden', 'true');

    const badge = document.createElement('span');
    badge.className = 'biavent-tour-tap-indicator-label';
    badge.textContent = label;

    const arrow = document.createElement('span');
    arrow.className = 'biavent-tour-tap-indicator-arrow';
    arrow.textContent = '↓';

    indicator.append(badge, arrow);
    document.body.appendChild(indicator);

    const reposition = () => {
      const rect = target.getBoundingClientRect();
      const isNavItem = target.closest('.bottom-navigation, .top-navigation') !== null;

      indicator.style.left = `${rect.left + rect.width / 2}px`;

      if (isNavItem) {
        indicator.style.top = `${rect.top - 10}px`;
        indicator.style.transform = 'translate(-50%, -100%)';
        arrow.style.display = 'block';
      } else {
        indicator.style.top = `${rect.bottom + 10}px`;
        indicator.style.transform = 'translate(-50%, 0)';
        arrow.textContent = '↑';
        arrow.style.display = 'block';
      }
    };

    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    const resizeObserver = new ResizeObserver(reposition);
    resizeObserver.observe(target);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      indicator.remove();
    };
  }

  private clearHighlightCleanup(): void {
    this.highlightCleanup?.();
    this.highlightCleanup = null;
  }

  private syncPreviousButtonState(
    previousButton: HTMLButtonElement,
    stepIndex: number,
  ): void {
    if (stepIndex <= 0) {
      return;
    }

    // Each engine step is a 1-step driver tour, so driver.js always disables "previous".
    previousButton.disabled = false;
    previousButton.classList.remove('driver-popover-btn-disabled');
    previousButton.classList.remove('biavent-tour-btn--disabled');
  }

  private applyNextButtonLabel(
    nextButton: HTMLButtonElement,
    isLast: boolean,
    t: AppTourEngineCallbacks['t'],
  ): void {
    // driver.js treats each engine step as a single-step tour, so it defaults to "Done".
    nextButton.textContent = isLast
      ? t('appTour.controls.done')
      : t('appTour.controls.next');
  }

  private syncNextButtonState(
    step: TourStepDef,
    nextButton: HTMLButtonElement,
    isLast: boolean,
  ): void {
    if (isLast) {
      nextButton.disabled = false;
      return;
    }

    if (step.advanceOn === 'route' && step.expectedRoute) {
      const canAdvance = matchesExpectedRoute(
        this.callbacks.getPathname(),
        step.expectedRoute,
      );
      nextButton.disabled = !canAdvance;
      nextButton.classList.toggle('biavent-tour-btn--disabled', !canAdvance);
    }
  }

  private isOnRoute(route: string): boolean {
    return matchesExpectedRoute(this.callbacks.getPathname(), route);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }
}
